import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useState
} from "react"
import {
  matchPath,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams
} from "react-router-dom"
import { BankEnum, CarEnum, ClientEnum } from "app/pages/appFlow/types"
import {
  buildPath,
  getCarTypeRedirect,
  hasCarTypeChoice,
  resolveCarType
} from "app/pages/appFlow/routing"
import routes from "router"

export interface AppflowContextType {
  clientType?: ClientEnum
  carType?: CarEnum
  product?: string
  step?: string
  lang?: string | null
  bank?: BankEnum
  path?: string
  navigate: (
    clientType?: ClientEnum,
    carType?: CarEnum,
    product?: string,
    step?: number
  ) => void
  goBack: () => void
  setBank: Dispatch<SetStateAction<BankEnum | undefined>>
}

export const AppflowContext = createContext<AppflowContextType | undefined>(
  undefined
)

export const AppflowProvider: React.FC = ({ children }) => {
  const [bank, setBank] = useState<BankEnum | undefined>(BankEnum.KBC)
  const { pathname, search } = useLocation()
  const [searchParams] = useSearchParams()
  const nativeNavigate = useNavigate()

  const appflowRoute = routes.find(
    ({ path }) => path === "/:clientType?/:carType?/:product?/:step?"
  )
  const matchedRoute = appflowRoute
    ? matchPath(appflowRoute.path!, pathname)
    : null

  const { clientType, carType, product, step } =
    (matchedRoute?.params as any) || {}
  const lang = searchParams.get("lang")

  // A missing or disabled car type in the URL is resolved for the visitor
  // instead of shown as a menu or a dead end, so old pre-owned links land on
  // the product list of the enabled car type.
  const redirectTo = getCarTypeRedirect({ clientType, carType }, search)

  useEffect(() => {
    if (product && !step && !redirectTo) {
      navigate(clientType, carType, product, 1)
    }
  }, [clientType, carType, product, step, redirectTo])

  const navigate = (
    clientType?: ClientEnum,
    carType?: CarEnum,
    product?: string,
    step?: number | string
  ) => {
    nativeNavigate(
      buildPath({
        clientType,
        carType: clientType ? resolveCarType(carType) : undefined,
        // A product without a step means the product list.
        product: step ? product : undefined,
        step,
        lang
      })
    )
  }

  const goBack = () => {
    const stepAsNumber = step ? parseInt(step, 10) : NaN

    if (
      clientType &&
      carType &&
      product &&
      !isNaN(stepAsNumber) &&
      stepAsNumber > 1
    ) {
      navigate(clientType, carType, product, stepAsNumber - 1)
      return
    }

    const params = [clientType, carType, product, step].filter(param => param)
    params.pop()

    // Without a car-type choice that menu is skipped, so back from the product
    // list goes to the client-type menu.
    if (params.length === 1 && !hasCarTypeChoice()) {
      params.pop()
    }

    navigate(params[0] as ClientEnum, params[1] as CarEnum, params[2])
  }

  const value = {
    clientType,
    carType,
    product,
    step: step,
    lang,
    bank,
    path: buildPath({ clientType, carType, product, step, lang }),
    setBank,
    navigate,
    goBack
  }

  // Redirect before anything below renders, so the layout and the GTM
  // page_view never see the URL that is being replaced.
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />
  }

  return (
    <AppflowContext.Provider value={value}>{children}</AppflowContext.Provider>
  )
}
